/**
 * Formatação em pt-BR.
 *
 * Centralizado porque data e dinheiro aparecem em dezenas de telas, e nada
 * denuncia mais um produto mal-acabado do que a mesma data escrita de três
 * jeitos diferentes em três páginas.
 */

const BRL = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
});

const BRL_ROUND = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 0,
});

/** Centavos → "R$ 29,90". Valores redondos perdem os centavos: "R$ 200". */
export function money(cents: number): string {
  return cents % 100 === 0 ? BRL_ROUND.format(cents / 100) : BRL.format(cents / 100);
}

/** Epoch (segundos) → "sex, 21/08 · 19h00". Null vira "a definir". */
export function dateTime(epochSeconds: number | null | undefined): string {
  if (!epochSeconds) return 'A definir';
  const d = new Date(epochSeconds * 1000);
  const day = d.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' });
  const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${day.replace('.', '')} · ${time.replace(':', 'h')}`;
}

/**
 * Só a hora, para quando a data já está dita pelo contexto.
 *
 * No calendário de uma edição, todas as partidas são no mesmo dia — repetir a
 * data em cada rodada só afasta o que interessa, que é o horário.
 */
export function timeOnly(epochSeconds: number | null | undefined): string | null {
  if (!epochSeconds) return null;
  return new Date(epochSeconds * 1000)
    .toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    .replace(':', 'h');
}

export function dateOnly(epochSeconds: number | null | undefined): string {
  if (!epochSeconds) return 'A definir';
  return new Date(epochSeconds * 1000).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** "há 3 minutos", "em 2 dias". Usado no status do coletor e nas rodadas. */
export function relative(epochSeconds: number | null | undefined): string {
  if (!epochSeconds) return '—';
  const diff = epochSeconds - Math.floor(Date.now() / 1000);
  const rtf = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' });
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31536000],
    ['month', 2592000],
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
  ];
  for (const [unit, seconds] of units) {
    if (Math.abs(diff) >= seconds) return rtf.format(Math.round(diff / seconds), unit);
  }
  return rtf.format(Math.round(diff), 'second');
}

/** Nota da EA vem ×100 no banco. 735 → "7.35". */
export function rating(x100: number | null | undefined): string {
  if (x100 === null || x100 === undefined) return '—';
  return (x100 / 100).toFixed(2);
}

/** Saldo de gols sempre com sinal: "+4", "-2", "0". */
export function signed(value: number): string {
  return value > 0 ? `+${value}` : String(value);
}

/** Porcentagem inteira a partir de acertos/tentativas, tolerando divisão por zero. */
export function percent(made: number | null, attempts: number | null): string {
  if (!attempts || made === null) return '—';
  return `${Math.round((made / attempts) * 100)}%`;
}

export const PLATFORM_LABEL = {
  ps5: 'PS5',
  'xbox-series': 'Xbox Series',
  pc: 'PC',
} as const;

export const STAGE_LABEL = {
  group: 'Fase de grupos',
  quarter: 'Quartas de final',
  semi: 'Semifinal',
  third_place: 'Disputa de 3º lugar',
  final: 'Grande final',
} as const;

export const STAGE_SHORT = {
  group: 'Grupos',
  quarter: 'Quartas',
  semi: 'Semi',
  third_place: '3º lugar',
  final: 'Final',
} as const;

export const STATUS_LABEL = {
  scheduled: 'Agendada',
  live: 'Ao vivo',
  awaiting_report: 'Aguardando resultado',
  disputed: 'Em revisão',
  finished: 'Encerrada',
  walkover: 'W.O.',
  cancelled: 'Cancelada',
} as const;

export const TOURNAMENT_STATUS_LABEL = {
  draft: 'Rascunho',
  registration: 'Inscrições abertas',
  ready: 'Aguardando sorteio',
  group_stage: 'Fase de grupos',
  knockout: 'Eliminatórias',
  finished: 'Encerrado',
  cancelled: 'Cancelado',
} as const;

/** Explica na UI qual critério definiu a posição — Regulamento 5.2. */
export const TIEBREAK_LABEL = {
  points: 'Pontos',
  goal_diff: 'Saldo de gols',
  goals_for: 'Gols marcados',
  head_to_head: 'Confronto direto',
  cards: 'Menos cartões',
  draw: 'Sorteio da organização',
} as const;
